import * as React from 'react';
import classnames from 'classnames';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';

import { Link } from 'libs/router';

import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import { __t, applyTranslations } from 'utils/language';
import { UNTITLED } from '../sov';
import { getReportModifiedDate } from '../dates/sov-dates';
import {
  CHANNELS,
  HASHTAG,
  HASHTAGS,
  MENTION,
  MENTIONS,
  TAG,
  TAGS
} from '../queries/sov-queries';

import type {
  SVGDynamicIconProps,
  SortableTableColumn,
  SortableTableColumnType
} from 'types';
import type {
  SOVIconEnriched,
  SOVQueryColumnChannelsCallback,
  SOVQueryColumnTextCallback,
  SOVQueryColumnTitleCallback,
  SOVQueryLocal,
  SOVQueryToggleCallback,
  SOVReport,
  SOVReportModalCallback
} from 'modules/insights/sov/types';

applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));
applyTranslations(require('modules/insights/sov/components/reports/sov-reports.i18n.json'));

export const getColumnReportName = (msgKey: string, className?: string): SortableTableColumnType => ({
  key: 'sov-table-column--name',
  title: __t(`${msgKey}.labels.name`),
  className,

  render: (col) => (
    <div
      className="text-overflow-ellipsis"
      title={col.name || UNTITLED}
    >
      <Link
        to={col.icons[0]?.link}
        className="sortable-table__link"
      >
        {col.name || UNTITLED}
      </Link>
    </div>
  )
});

export const getColumnReportDescription = (msgKey: string, className?: string): SortableTableColumnType => ({
  key: 'sov-table-column--description',
  title: __t(`${msgKey}.labels.description`),
  className,

  render: (report: SOVReport) => (
    <div
      className="text-overflow-ellipsis"
      title={report.description}
    >
      {report.description}
    </div>
  )
});

export const getColumnReportDate = (className?: string): SortableTableColumnType => ({
  key: 'sov-table-column--date',
  title: '',
  className,

  render: (report: SOVReport) => {
    const dateModified = getReportModifiedDate(report.createdAt, report.updatedAt, true);

    return (
      <div
        className="text-overflow-ellipsis"
        title={`${String(dateModified)} UTC`}
      >
        {dateModified}
      </div>
    );
  }
});

export const getColumnIconProps = (icon: SOVIconEnriched): SVGDynamicIconProps => ({
  name: icon.name,
  width: icon.size.width,
  height: icon.size.height,
  ...(!isNil(icon.link) ? { link: icon.link } : null),
  clickable: true,
  customColor: 'grey',

  data: {
    'action-type': icon.name,
    'report-id': icon.report?.id
  }
});

export const getColumnIcons = (modalCallback: SOVReportModalCallback, className?: string): SortableTableColumnType => ({
  key: 'sov-table-column--icons',
  title: '',
  className,
  display: 'flex',
  width: 128,

  render: (col) => (
    col.icons.map(icon => {
      const iconProps = getColumnIconProps(icon);

      return (
        <div
          key={`sov-table-column__icon--${icon.id}-${col.id}`}
          className="sortable-table__cell-icon"
        >
          {
            isNil(iconProps.link) && isFunction(modalCallback) ?
              modalCallback(icon, iconProps) :
              <Tooltip
                message={icon.tooltip}
                width="micro"
              >
                <SVGDynamicIcon {...iconProps} />
              </Tooltip>
          }
        </div>
      );
    })
  )
});

export const getColumnCheckbox = (toggleCallback: SOVQueryToggleCallback, className?: string): SortableTableColumn => ({
  key: 'sov-table-column--checkbox',
  title: '',
  className,

  render: (query: SOVQueryLocal) => (
    <InputCheckbox
      key={`sov-table-column__checkbox-${String(query.id)}`}
      id={`sov-table-column__checkbox-${String(query.id)}`}
      name="enabled"
      value={String(query.enabled)}
      checked={query.enabled}
      onChange={(event) => toggleCallback(event, query)}
    />
  )
});

export const getColumnKeyword = (msgKey: string, className?: string): SortableTableColumn => ({
  key: 'sov-table-column--keyword',
  title: __t(`${msgKey}.labels.keyword`),
  className,

  render: (query: SOVQueryLocal) => {
    const textClasses = classnames(className, 'text-overflow-ellipsis', {
      [`${className}--disabled`]: !query.enabled
    });

    return (
      <div
        className={textClasses}
        title={query.name}
      >
        {query.name}
      </div>
    );
  }
});

export const getColumnHashtags = (
  columnTitleCallback: SOVQueryColumnTitleCallback,
  columnTextCallback: SOVQueryColumnTextCallback,
  className?: string
): SortableTableColumn => ({
  key: `sov-table-column--${HASHTAGS}`,
  title: columnTitleCallback(HASHTAGS),
  className,

  render: (query: SOVQueryLocal) => (
    columnTextCallback(query, HASHTAG)
  )
});

export const getColumnMentions = (
  columnTitleCallback: SOVQueryColumnTitleCallback,
  columnTextCallback: SOVQueryColumnTextCallback,
  className?: string
): SortableTableColumn => ({
  key: `sov-table-column--${MENTIONS}`,
  title: columnTitleCallback(MENTIONS),
  className,

  render: (query: SOVQueryLocal) => (
    columnTextCallback(query, MENTION)
  )
});

export const getColumnTags = (
  columnTitleCallback: SOVQueryColumnTitleCallback,
  columnTextCallback: SOVQueryColumnTextCallback,
  className?: string
): SortableTableColumn => ({
  key: `sov-table-column--${TAGS}`,
  title: columnTitleCallback(TAGS),
  className,

  render: (query: SOVQueryLocal) => (
    columnTextCallback(query, TAG)
  )
});

export const getColumnChannels = (
  columnTitleCallback: SOVQueryColumnTitleCallback,
  channelColumnTextCallback: SOVQueryColumnChannelsCallback,
  className?: string
): SortableTableColumn => ({
  key: `sov-table-column--${CHANNELS}`,
  title: columnTitleCallback(CHANNELS),
  className,

  render: (query) => (
    channelColumnTextCallback(query)
  )
});
